import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { agentService } from "../../services/agentService";
import {
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

const AgentDashboard = () => {
  const { user } = useAuth();
  const [pendingValidations, setPendingValidations] = useState([]);
  const [validationHistory, setValidationHistory] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [pendingData, validationData, deliveryData] = await Promise.all([
          agentService.getPendingValidations(),
          agentService.getValidationHistory(),
          agentService.getDeliveryHistory(),
        ]);

        // Extract data from API responses
        const pendingValidationsData = pendingData?.data?.bins || pendingData?.bins || [];
        const validationHistoryData = validationData?.data?.bins || validationData?.bins || [];
        const deliveryHistoryData = deliveryData?.data?.deliveries || deliveryData?.deliveries || [];

        setPendingValidations(pendingValidationsData);
        setValidationHistory(validationHistoryData.slice(0, 5)); // Get last 5
        setDeliveryHistory(deliveryHistoryData.slice(0, 5)); // Get last 5
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = [
    {
      name: "Pending Validations",
      value: pendingValidations.length,
      icon: ClockIcon,
      color: "bg-yellow-500",
      href: "/agent/pending-validations",
    },
    {
      name: "Validated Bins",
      value: validationHistory.length,
      icon: CheckCircleIcon,
      color: "bg-green-500",
      href: "/agent/validation-history",
    },
    {
      name: "Deliveries",
      value: deliveryHistory.length,
      icon: TruckIcon,
      color: "bg-blue-500",
      href: "/agent/delivery-history",
    },
    {
      name: "Total Earnings",
      value: `₦${deliveryHistory
        .reduce((sum, delivery) => sum + (delivery.totalProfit || 0), 0)
        .toFixed(2)}`,
      icon: CurrencyDollarIcon,
      color: "bg-purple-500",
      href: "/agent/delivery-history",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name || user?.username}!
        </h1>
        <p className="text-gray-600 mt-2">
          You're an authorized collection agent. Help sellers validate their
          materials and earn commissions.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/agent/pending-validations"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ClockIcon className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Pending Validations</h3>
              <p className="text-sm text-gray-600">Review and validate bins</p>
            </div>
          </Link>

          <Link
            to="/agent/validation-history"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Validation History</h3>
              <p className="text-sm text-gray-600">View validated bins</p>
            </div>
          </Link>

          <Link
            to="/agent/delivery-history"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TruckIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Delivery History</h3>
              <p className="text-sm text-gray-600">
                Track deliveries and earnings
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Validations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Pending Validations
          </h2>
          {pendingValidations.length > 0 ? (
            <div className="space-y-4">
              {pendingValidations.slice(0, 3).map((validation) => (
                <div
                  key={validation._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      Bin #{validation._id?.slice(-6) || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Seller: {validation.user?.firstName} {validation.user?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(validation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Link
                    to="/agent/pending-validations"
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Validate
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">
              No pending validations at the moment.
            </p>
          )}
        </div>

        {/* Recent Deliveries */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Deliveries
          </h2>
          {deliveryHistory.length > 0 ? (
            <div className="space-y-4">
              {deliveryHistory.slice(0, 3).map((delivery) => (
                <div
                  key={delivery._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      Delivery #{delivery._id?.slice(-6) || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(delivery.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      Qty: {delivery.totalQuantity}kg
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      ₦{delivery.totalProfit?.toFixed(2) || "0.00"}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        delivery.pickupStatus === "Delivered"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {delivery.pickupStatus}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">
              No deliveries yet. Start by validating bins!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
